import { type ReactNode } from 'react';
import {
  Box,
  Button,
  Divider,
  HeadingText,
  Icon,
  Modal,
  ParagraphText,
  queryHelpers,
  StackLayout,
  TextAction,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { INTERNAL_LINKS } from 'common/links';
import { useNavigate } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

const linkCss = {
  typography: '$bodyTwo',
  textDecoration: 'underline',
} as const;

type GuidanceRowProps = {
  icon: 'descriptivePersonalInfo' | 'descriptivePeople';
  label: string;
  description: string;
};

const renderBold = (chunks: ReactNode) => (
  <strong style={{ fontWeight: 700 }}>{chunks}</strong>
);

const GuidanceRow = ({ icon, label, description }: GuidanceRowProps) => (
  <Box as="li" css={{ display: 'flex', gap: '$threeQuarters' }}>
    <Icon icon={icon} size="$oneAndHalf" label="" css={{ flexShrink: 0 }} />
    <Box>
      <HeadingText
        as="h3"
        level="3"
        size="md"
        css={{ typography: '$subtitleOne' }}
      >
        {label}
      </HeadingText>
      <Box as="p" css={{ typography: '$bodyTwo', marginBlock: '$quarter' }}>
        {description}
      </Box>
    </Box>
  </Box>
);

type SignInFooterProps = {
  showGuidance?: boolean;
};

export const SignInFooter = ({ showGuidance = false }: SignInFooterProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  let minHeight: string | undefined;
  if (isMobile && !showGuidance) {
    minHeight = 'calc(60svh - 200px)';
  }

  let footerMarginTop: string | undefined;
  if (!showGuidance) {
    footerMarginTop = isMobile ? '-$threeQuarters' : '-$oneAndHalf';
  }

  return (
    <StackLayout
      horizontalAlignment="center"
      css={{
        flexGrow: 1,
        paddingBottom: '$two',
        width: isMobile ? '327px' : '482px',
        minHeight,
        marginTop: footerMarginTop,
      }}
    >
      {showGuidance && (
        <Divider
          css={{
            width: '100%',
            marginBottom: '$three',
            borderColor: '$onSurfaceBorderDefault',
            borderTopWidth: '2px',
          }}
        />
      )}
      {showGuidance && (
        <Box
          as="section"
          css={{
            width: '100%',
            paddingBottom: '$five',
          }}
        >
          <HeadingText
            as="h2"
            level="2"
            size="md"
            css={{ marginBottom: '$half' }}
          >
            {formatMessage({ id: 'STR_SIGN_IN_GUIDANCE_HEADING' })}
          </HeadingText>
          <ParagraphText css={{ typography: '$bodyTwo', marginBottom: '$one' }}>
            {formatMessage({ id: 'STR_SIGN_IN_GUIDANCE_BODY' })}
          </ParagraphText>
          <Box
            as="ul"
            css={{
              listStyle: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '$one',
            }}
          >
            <GuidanceRow
              icon="descriptivePersonalInfo"
              label={formatMessage({
                id: 'STR_SIGN_IN_GUIDANCE_PATIENT_LABEL',
              })}
              description={formatMessage({
                id: 'STR_SIGN_IN_GUIDANCE_PATIENT_DESCRIPTION',
              })}
            />
            <GuidanceRow
              icon="descriptivePeople"
              label={formatMessage({
                id: 'STR_SIGN_IN_GUIDANCE_CAREGIVER_LABEL',
              })}
              description={formatMessage({
                id: 'STR_SIGN_IN_GUIDANCE_CAREGIVER_DESCRIPTION',
              })}
            />
          </Box>
        </Box>
      )}
      {!showGuidance && (
        <Modal.Root>
          <Modal.Trigger asChild>
            <TextAction
              as="button"
              aria-haspopup="dialog"
              css={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '$half',
                marginBottom: '$one',
                typography: '$buttonTwo',
                color: '$onSurfaceTextPrimary',
              }}
            >
              {formatMessage({ id: 'STR_CARING_FOR_SOMEONE' })}
              <Icon
                icon="tinyHelper"
                size="$one"
                label=""
                tint="$onSurfaceIconPrimary"
              />
            </TextAction>
          </Modal.Trigger>
          <Modal.Content
            padding={
              isMobile
                ? '$twoAndHalf $oneAndHalf $oneAndHalf'
                : '$twoAndHalf $four $one'
            }
            width={668}
            showCloseButton={false}
            css={{ position: 'relative' }}
          >
            <Modal.Close asChild>
              <Button
                priority="tertiary"
                quiet
                icon="tinyClose"
                hideLabel
                size="small"
                css={{
                  position: 'absolute',
                  top: '$half',
                  insetInlineEnd: '$half',
                }}
              >
                {formatMessage({ id: 'STR_CLOSE_DIALOG' })}
              </Button>
            </Modal.Close>
            <Modal.Title>
              <HeadingText
                as="h2"
                size="lg"
                level="2"
                css={{ marginBottom: '$one' }}
              >
                {formatMessage({ id: 'STR_CARING_FOR_SOMEONE' })}
              </HeadingText>
            </Modal.Title>
            <ParagraphText css={{ marginBlockEnd: '$half !important' }}>
              {formatMessage(
                { id: 'STR_CAREGIVER_MODAL_BODY' },
                { bold: renderBold },
              )}
            </ParagraphText>
            <ParagraphText css={{ marginBlockEnd: '$two !important' }}>
              {formatMessage(
                { id: 'STR_CAREGIVER_MODAL_NOT_A_CAREGIVER' },
                { bold: renderBold },
              )}
            </ParagraphText>
            <Button
              priority="primary"
              size="large"
              css={{
                width: '100%',
                marginBottom: '$half',
                justifyContent: 'center',
              }}
              onClick={() => navigate(APP_MANAGED_ROUTES.SIGN_IN)}
            >
              {formatMessage({ id: 'STR_CONTINUE_TO_MYCHART' })}
            </Button>
            <Modal.Close asChild>
              <Button
                size="large"
                priority="secondary"
                css={{
                  width: '100%',
                  border: 'none',
                  backgroundColor: 'transparent',
                  justifyContent: 'center',
                }}
              >
                {formatMessage({ id: 'STR_CLOSE' })}
              </Button>
            </Modal.Close>
          </Modal.Content>
        </Modal.Root>
      )}
      <ParagraphText
        css={{
          textAlign: 'center',
          typography: '$bodyTwo',
          ...(isMobile
            ? { marginTop: 'auto' }
            : {
                [`@media (min-height: 700px)`]: {
                  position: 'fixed',
                  bottom: '$two',
                  insetInlineStart: '50%',
                  insetInlineEnd: 0,
                },
              }),
        }}
      >
        <TextAction href={INTERNAL_LINKS.POLICY} css={linkCss}>
          {formatMessage({ id: 'STR_PRIVACY_POLICY' })}
        </TextAction>
        {` ${formatMessage({ id: 'STR_AND' })} `}
        <TextAction href={INTERNAL_LINKS.TERMS} css={linkCss}>
          {formatMessage({ id: 'STR_TERMS_OF_SERVICE' })}
        </TextAction>
      </ParagraphText>
    </StackLayout>
  );
};
